import { useEffect } from "react";

interface UseInlineFieldUndoOptions {
  /** Undo function from useInlineFieldSave */
  undoField: (fieldname: string) => Promise<boolean>;
  /** Check if a field is undoable (within 5s window) */
  getUndoable: (fieldname: string) => boolean;
  /** The last successfully saved field name */
  lastSavedField: string | null;
  /** Called after successful undo to update local form state */
  onUndo?: (fieldname: string) => void;
}

/**
 * Listen for Cmd+Z / Ctrl+Z to undo the last inline field save.
 *
 * Only fires when:
 * - No input/textarea is currently focused (avoids conflict with text editing undo)
 * - A field was recently saved (lastSavedField is set)
 * - The field is still undoable (within 5s window)
 *
 * One-shot: after undo, the field is no longer undoable.
 */
export function useInlineFieldUndo({
  undoField,
  getUndoable,
  lastSavedField,
  onUndo,
}: UseInlineFieldUndoOptions) {
  useEffect(() => {
    if (!lastSavedField) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        // Don't fire if an input/textarea is focused (let browser handle text undo)
        const active = document.activeElement;
        if (
          active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement ||
          (active instanceof HTMLElement && active.isContentEditable)
        ) {
          return;
        }

        if (lastSavedField && getUndoable(lastSavedField)) {
          e.preventDefault();
          const fieldToUndo = lastSavedField;
          undoField(fieldToUndo).then((success) => {
            if (success && onUndo) onUndo(fieldToUndo);
          });
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [lastSavedField, undoField, getUndoable, onUndo]);
}
